import React, { useMemo, forwardRef, useImperativeHandle } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Box, FormControl, FormHelperText, InputLabel } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledFormControl = styled(FormControl)(({ theme, error }) => ({
  '& .ql-editor': {
    minHeight: '120px',
    fontSize: '14px',
    fontFamily: theme.typography.fontFamily,
    lineHeight: 1.5,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
  },
  '& .ql-toolbar': {
    borderTop: `1px solid ${theme.palette.divider}`,
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    backgroundColor: theme.palette.action.hover,
  },
  '& .ql-container': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
    ...(error && {
      borderColor: theme.palette.error.main,
      '&:hover': {
        borderColor: theme.palette.error.main,
      },
    }),
  },
  '& .ql-editor.ql-blank::before': {
    fontStyle: 'normal',
    color: theme.palette.text.secondary,
  },
}));

const RichTextEditor = forwardRef(({
  value = '',
  onChange,
  label,
  placeholder = 'Enter text...',
  error = false,
  helperText,
  required = false,
  disabled = false,
  minHeight = '120px',
  maxHeight = '300px',
  toolbarConfig = 'full',
  ...props
}, ref) => {
  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    getEditor: () => {
      return quillRef.current?.getEditor();
    },
    focus: () => {
      quillRef.current?.focus();
    },
    blur: () => {
      quillRef.current?.blur();
    },
  }));

  const quillRef = React.useRef();

  // Toolbar configurations
  const toolbarConfigs = useMemo(() => ({
    minimal: [
      ['bold', 'italic', 'underline'],
      ['link'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ],
    basic: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
    full: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      ['blockquote', 'code-block'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
    description: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      [{ 'align': [] }],
      ['clean']
    ]
  }), []);

  // Custom link handler
  const handleLinkClick = (value) => {
    const url = prompt('Enter URL:');
    if (url) {
      const editor = quillRef.current?.getEditor();
      if (editor) {
        const range = editor.getSelection();
        if (range) {
          editor.formatText(range.index, range.length, 'link', url);
        } else {
          editor.insertText(editor.getLength(), url);
          editor.formatText(editor.getLength() - url.length, url.length, 'link', url);
        }
      }
    }
    return false; // Prevent default behavior
  };

  // Custom toolbar with enhanced link functionality
  const customToolbar = useMemo(() => {
    const config = toolbarConfigs[toolbarConfig] || toolbarConfigs.full;
    
    return {
      toolbar: {
        container: config,
        handlers: {
          link: handleLinkClick,
        }
      }
    };
  }, [toolbarConfig, toolbarConfigs]);

  const handleChange = (content, delta, source, editor) => {
    if (onChange) {
      onChange(content);
    }
  };

  return (
    <StyledFormControl 
      fullWidth 
      error={error}
      disabled={disabled}
      {...props}
    >
      {label && (
        <InputLabel 
          shrink 
          required={required}
          sx={{ 
            backgroundColor: 'background.paper',
            px: 1,
            ml: 1,
            zIndex: 1,
            position: 'absolute',
            top: -8,
            left: 8
          }}
        >
          {label}
        </InputLabel>
      )}
      <Box sx={{ mt: label ? 2 : 0 }}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          readOnly={disabled}
          modules={customToolbar}
          style={{
            minHeight,
            maxHeight,
            overflow: 'auto'
          }}
        />
      </Box>
      {helperText && (
        <FormHelperText error={error}>
          {helperText}
        </FormHelperText>
      )}
    </StyledFormControl>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
